// Divergência entre previsto e realizado — SELO, nunca portão.
//
// Rizoma_Manejo_Operacoes_v2_Spec.md §5: «"Revisar" deixa de ser status:
// divergência grande (realizado < 70% ou > 130% do previsto) vira um **selo** na
// operação concluída, não uma fila».
//
// 🔴 A regra que este arquivo existe para proteger: divergir NUNCA recusa a
// conclusão. Na v1 a divergência mandava a operação para uma fila de "Revisar"
// que ninguém esvaziava — 26 operações em produção, todas paradas lá. O operador
// aplicou o que aplicou; o número dele é o fato. O selo serve para alguém OLHAR
// depois, não para impedir o registro na hora.
//
// Mora em shared/ porque o servidor calcula (para o payload do evento `concluida`,
// que é o que o histórico e o boletim vão ler) e a tela mostra. Duas
// implementações divergiriam no limite — e "está divergente aqui e não lá" é
// exatamente o tipo de bug que faz o usuário parar de confiar no selo.

/** Abaixo disso, aplicou-se bem menos que o planejado. */
export const DIVERGENCIA_MIN = 0.7;
/** Acima disso, bem mais. */
export const DIVERGENCIA_MAX = 1.3;

export interface ItemParaDivergencia {
  mnjProdutoId: string;
  /** Nome do produto — só para a frase do selo; ausente vira "item". */
  produtoNome?: string | null;
  unidade?: string | null;
  qtdPrevista: number;
  /** `null` = não informado; item sem realizado nunca é divergente. */
  qtdRealizada: number | null;
}

export interface DivergenciaItem {
  mnjProdutoId: string;
  produtoNome: string | null;
  unidade: string | null;
  qtdPrevista: number;
  qtdRealizada: number;
  /** realizado ÷ previsto. `1` = exato. */
  razao: number;
  /** Percentual arredondado, para exibição: 42 = aplicou 42% do previsto. */
  percentual: number;
}

export interface ResultadoDivergencia {
  divergente: boolean;
  itens: DivergenciaItem[];
}

/**
 * Quais itens saíram da faixa 70–130%.
 *
 * Previsto zero (ou negativo) é ignorado de propósito: a razão seria infinita e o
 * selo diria "3000% do previsto" para uma operação que nunca planejou quantidade
 * nenhuma. Item sem realizado também sai — não informar não é divergir.
 */
export function calcularDivergencia(itens: ItemParaDivergencia[]): ResultadoDivergencia {
  const fora: DivergenciaItem[] = [];
  for (const it of itens) {
    if (!(it.qtdPrevista > 0)) continue;
    if (it.qtdRealizada === null || it.qtdRealizada === undefined) continue;
    const razao = it.qtdRealizada / it.qtdPrevista;
    if (razao >= DIVERGENCIA_MIN && razao <= DIVERGENCIA_MAX) continue;
    fora.push({
      mnjProdutoId: it.mnjProdutoId,
      produtoNome: it.produtoNome ?? null,
      unidade: it.unidade ?? null,
      qtdPrevista: it.qtdPrevista,
      qtdRealizada: it.qtdRealizada,
      razao,
      percentual: Math.round(razao * 100),
    });
  }
  return { divergente: fora.length > 0, itens: fora };
}

/**
 * Frase curta do selo: "Ureia 42% do previsto" ou "2 itens fora do previsto".
 * Devolve `null` quando não há divergência — quem chama não precisa checar duas
 * coisas.
 */
export function resumirDivergencia(resultado: ResultadoDivergencia): string | null {
  if (!resultado.divergente) return null;
  if (resultado.itens.length === 1) {
    const it = resultado.itens[0];
    return `${it.produtoNome ?? "Item"} ${it.percentual}% do previsto`;
  }
  return `${resultado.itens.length} itens fora do previsto`;
}
